import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaNewspaper, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import { supabase } from '../lib/supabase';

const AdminNews = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | published | draft

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('news')
      .select('*')
      .order('created_at', { ascending: false });
    setArticles(data || []);
    setLoading(false);
  };

  const togglePublish = async (article) => {
    const { error } = await supabase
      .from('news')
      .update({ is_published: !article.is_published, updated_at: new Date().toISOString() })
      .eq('id', article.id);
    if (!error) {
      setArticles(prev => prev.map(a => a.id === article.id ? { ...a, is_published: !a.is_published } : a));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus artikel ini?')) return;
    const { error } = await supabase.from('news').delete().eq('id', id);
    if (!error) setArticles(prev => prev.filter(a => a.id !== id));
  };

  const filtered = articles.filter(a => {
    const matchSearch = a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.category.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'published' ? a.is_published : !a.is_published);
    return matchSearch && matchFilter;
  });

  const counts = {
    all: articles.length,
    published: articles.filter(a => a.is_published).length,
    draft: articles.filter(a => !a.is_published).length,
  };

  const categoryColor = (cat) => {
    const map = {
      Prestasi: 'bg-green-100 text-green-700',
      Event: 'bg-blue-100 text-blue-700',
      Kolaborasi: 'bg-purple-100 text-purple-700',
      Program: 'bg-amber-100 text-amber-700',
      Umum: 'bg-gray-100 text-gray-700',
    };
    return map[cat] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <FaNewspaper className="text-gold-400" /> Kelola Berita
            </h1>
            <p className="text-gray-300">Tulis, edit, dan publish berita alumni PIMNAS</p>
          </div>
          <Link
            to="/admin/news/new"
            className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-sm"
          >
            <FaPlus /> Tulis Artikel Baru
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter tabs */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'Semua', count: counts.all },
              { key: 'published', label: 'Published', count: counts.published },
              { key: 'draft', label: 'Draft', count: counts.draft },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${filter === f.key ? 'bg-primary-600 text-white shadow' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Cari artikel..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FaNewspaper className="text-5xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Belum ada artikel</h3>
            <p className="text-gray-400 mb-6">Mulai tulis artikel pertamamu!</p>
            <Link to="/admin/news/new" className="text-primary-600 font-medium hover:underline">
              + Tulis Artikel Baru
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(article => (
              <div key={article.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  {/* Cover thumbnail */}
                  {article.cover_url && (
                    <div className="md:w-48 h-32 md:h-auto shrink-0">
                      <img src={article.cover_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${categoryColor(article.category)}`}>
                            {article.category}
                          </span>
                          {article.is_published ? (
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-700">Published</span>
                          ) : (
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-200 text-gray-600">Draft</span>
                          )}
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <FaCalendarAlt /> {new Date(article.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">{article.title}</h3>
                        {article.excerpt && <p className="text-sm text-gray-500 line-clamp-2">{article.excerpt}</p>}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => togglePublish(article)}
                          className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded-lg transition ${article.is_published ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' : 'text-green-700 bg-green-50 hover:bg-green-100'}`}
                          title={article.is_published ? 'Unpublish' : 'Publish'}
                        >
                          {article.is_published ? <><FaEyeSlash /> Unpublish</> : <><FaEye /> Publish</>}
                        </button>
                        <Link
                          to={`/admin/news/edit/${article.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 px-3 py-2 rounded-lg transition"
                        >
                          <FaEdit /> Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition"
                        >
                          <FaTrash /> Hapus
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNews;
